import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { Modal } from 'reactstrap';
import getDeviceTypeMobile from '../../selectors/getDeviceTypeMobile';
import { setCurrentModal, selectCurrentModalId } from '../../thunks/setCurrentModal';
import { MESSAGE_MODAL_ID } from '../MessageModal';
import { ITEM_CART_AVAILABILITY_MODAL } from '../ItemCartAvailabilityModal';
import ModalHeader from '../../components/ModalHeader';
import StandardButton from '../../components/StandardButton';
import { OrderingSelectors, OrderingOperations, OrderingConstants } from 'polygon-ordering';
import { addToRecentLocations, clearRecentLocations } from '../../slices/recentLocations';
import { applyBufferDeliveryEstimate } from '../../thunks/applyBufferDeliveryEstimate';
import { confirmLocationEstimate } from '../../thunks/confirmLocationEstimate';
import { performDeliveryEstimate } from '../../thunks/performDeliveryEstimate';
import { adjustOrder } from '../../thunks/adjustOrder';
import { updateLoadingDependencies } from '../../slices/loadingDependencies';
import combineStyles from '../../utils/combineStyles';
import { isLocationInEstimates } from '../../utils/isLocationInEstimates';
import { setDesiredDeliveryAddress } from '../../slices/desiredDeliveryAddress';
import { DeliveryTimeAndAddressSelection } from '../../components/DeliveryTimeAndAddressSelection';
import { AddressLookupAndConfirmation } from '../../components/AddressLookupAndConfirmation';
import { DeliveryDetailsConfirmation } from '../../components/DeliveryDetailsConfirmation';
import AddressInputForm from '../../components/AddressInputForm';
import { useBottomSheet } from '../../providers';
import { saveAddress } from '../../libs/polygon-ordering/src/operations';
import {
  resetForm,
  setSelectedAddress,
  updateForm,
  replaceSelectedAddress,
} from '../../slices/deliveryAddressForm';
import { setInlineAlert } from '../../slices/inlineAlert';
import type {
  BackButtonParams,
  ContinueButtonDisabledParams,
  ContinueButtonParams,
  ContinueButtonTextParams,
  DeliveryDetailsModalAction,
} from './utils';
import {
  deepEqualIgnoringUndefined,
  getBackButtonActionPlan,
  getContinueButtonActionPlan,
  getContinueButtonText,
  getModalTitleText,
  isContinueButtonDisabled,
  isContinueButtonLoading,
} from './utils';
import { getDeliveryAddressFromPlaceIdAsync } from '../../libs/polygon-utils/src/GoogleMapsApiHandler';
import poweredByGoogleSvg from '../../images/powdered-by-google.svg';
import { setBufferDeliveryTime } from '../../libs/polygon-ordering/src/reducers/buffer/deliveryTime';
import { saleTypeSelected } from '../../utils/analytics';
import { BottomSheetProvider } from '../../providers';
import { useTranslation } from 'react-i18next';
import DiscardChangesModal from '../../components/DiscardChangesModal';
import { BOTTOM_SHEET_ANIMATION_DURATION_MS } from '../../providers/BottomSheetProvider';
import { store } from '../../store';

export const DELIVERY_DETAILS_MODAL_ID = 'DELIVERY_DETAILS_MODAL_ID';
const { SALE_TYPE } = OrderingConstants;
const {
  getBufferDeliveryTime,
  getDeliveryReadyToApply,
  getDeliveryRequiresLocationSelection,
  getKeyOrderPropertyUpdateInProgress,
  getLocationDeliveryEstimates,
  getLocationsForDisplay,
  getLocation,
} = OrderingSelectors;

const { setDeliveryNotes, setDeliveryDetails } = OrderingOperations;

const Content = () => {
  const dispatch = useAppDispatch();
  const deviceTypeMobile = useAppSelector(getDeviceTypeMobile);
  const { open, close } = useBottomSheet();
  const selectedAddress = useAppSelector(state => state.deliveryAddressForm.selectedAddress);

  const keyOrderPropertyUpdateInProgress = useAppSelector(getKeyOrderPropertyUpdateInProgress);
  const bufferDeliveryTime = useAppSelector(getBufferDeliveryTime);
  const desiredDeliveryTime = useAppSelector(state => state.desiredDeliveryTime);
  const effectiveDeliveryAddress = useAppSelector(state => state.desiredDeliveryAddress);
  const effectiveDeliveryTime = desiredDeliveryTime || bufferDeliveryTime;
  const deliveryReadyToApply = useAppSelector(getDeliveryReadyToApply);
  const deliveryRequiresLocationSelection = useAppSelector(getDeliveryRequiresLocationSelection);
  const locations = useAppSelector(getLocationsForDisplay);
  const locationDeliveryEstimates = useAppSelector(getLocationDeliveryEstimates);
  const deliveryEstimateFailed = useAppSelector(state => state.deliveryEstimateFailed);
  const googleMapsApiKey = useAppSelector(state => state.config.googleMapsApiKey);
  const modalParams = useAppSelector(state => state.modalParams);
  const formState = useAppSelector(state => state.deliveryAddressForm);
  const saleType = modalParams?.saleType || SALE_TYPE.DELIVERY;
  const [selectedLocationId, setSelectedLocationId] = useState<null | string>(null);
  const recentAddresses = useAppSelector(state => state.recentLocations);
  const deliveryTimeSelected = !!useAppSelector(getBufferDeliveryTime);
  const location = useAppSelector(getLocation);
  const { t } = useTranslation();

  const buildUpdatedAddress = (
    values: {
      streetAddress: string;
      unit: string;
      city: string;
      country: string;
      postCode: string;
      formattedAddress: string;
    },
    baseAddress: DeliveryAddress | null,
  ): DeliveryAddress => ({
    ...(baseAddress || {}),
    buildingInformation: values.unit,
    displayAddress: {
      firstLine: values.streetAddress,
      secondLine: [values.city, values.postCode].filter(Boolean).join(' '),
    },
    formattedAddress: values.formattedAddress,
    addressComponents: {
      ...(baseAddress?.addressComponents || {}),
      suburb: values.city,
      country: values.country,
      postCode: values.postCode,
    },
  });

  const handleOpenAddressInput = () => {
    const initialValues = {
      streetAddress: selectedAddress?.displayAddress?.firstLine || '',
      formattedAddress: selectedAddress?.formattedAddress || '',
      unit: selectedAddress?.buildingInformation || '',
      city: selectedAddress?.addressComponents?.suburb || '',
      country: selectedAddress?.addressComponents?.country || '',
      postCode: selectedAddress?.addressComponents?.postCode || '',
    };

    // Track the current form values in memory so we can check for unsaved changes when the modal closes
    let currentFormValues = initialValues;

    const handleSave = (values: typeof initialValues) => {
      const hasAddressChanged = !deepEqualIgnoringUndefined({
        initialValues,
        updatedValues: values,
      });
      // Don't update anything if nothing changed
      if (hasAddressChanged) {
        const updatedAddress = buildUpdatedAddress(values, selectedAddress || null);
        dispatch(setSelectedAddress(updatedAddress));
      }
      close();
    };

    const handleEditDeliveryAddressClosed = () => {
      // Check if user made any changes to the form before closing
      const hasAddressChanged = !deepEqualIgnoringUndefined({
        initialValues,
        updatedValues: currentFormValues,
      });
      if (hasAddressChanged) {
        /**
         * Close the Address Input modal then show the Discard Changes modal
         *
         * I tried to make `close` awaitable so we can do this:
         *
         * await close();
         * open({...})
         *
         * but it didn't work so we're using setTimeouts instead.
         */
        close();
        setTimeout(() => {
          open({
            title: t('discardChangesModal.title'),
            content: (
              <DiscardChangesModal
                onCancel={() => {
                  // Close the Discard Changes modal then show the Address Input modal again
                  close();
                  setTimeout(() => {
                    open({
                      title: t('title.modal.editDeliveryAddress'),
                      content: (
                        <AddressInputForm
                          initialValues={currentFormValues}
                          onSave={handleSave}
                          onChange={handleChange}
                        />
                      ),
                      onClose: handleEditDeliveryAddressClosed,
                    });
                  }, BOTTOM_SHEET_ANIMATION_DURATION_MS + 200);
                }}
                // Just close the modal if discarding changes is approved
                onDiscard={close}
              />
            ),
            hideCloseButton: true,
          });
        }, BOTTOM_SHEET_ANIMATION_DURATION_MS + 200);
      } else {
        // No changes were made, just close the modal
        close();
      }
    };

    const handleChange = (values: typeof initialValues) => {
      // Update currentFormValues whenever the form changes so we can check for unsaved changes later
      currentFormValues = values;
    };

    open({
      title: t('title.modal.editDeliveryAddress'),
      content: (
        <AddressInputForm
          initialValues={initialValues}
          onSave={handleSave}
          onChange={handleChange}
        />
      ),
      // Custom close handler that prompts user if there are unsaved changes
      onClose: handleEditDeliveryAddressClosed,
    });
  };

  useEffect(() => {
    if (locationDeliveryEstimates.length === 1) {
      const locationId = locations[locationDeliveryEstimates[0]?.locationId]?.id;
      if (locationId) {
        setSelectedLocationId(locationId);
      }
    }
  }, [locationDeliveryEstimates]);

  // A background re-estimate can replace locationDeliveryEstimates while a
  // location is already selected; clear the stale selection so the Continue
  // button doesn't silently act on a location that's no longer valid.
  useEffect(() => {
    const shouldResetSelectedLocation =
      selectedLocationId && !isLocationInEstimates(locationDeliveryEstimates, selectedLocationId);
    if (shouldResetSelectedLocation) {
      setSelectedLocationId(null);
    }
  }, [locationDeliveryEstimates]);

  useEffect(() => {
    if (!effectiveDeliveryAddress || !effectiveDeliveryTime) {
      dispatch(setInlineAlert({ visible: false }));
      return;
    }
    dispatch(
      performDeliveryEstimate({
        deliveryAddress: effectiveDeliveryAddress,
        desiredDeliveryTime: effectiveDeliveryTime,
        saleType,
      }),
    );
  }, [effectiveDeliveryAddress, effectiveDeliveryTime]);

  const processActions = async (actions: DeliveryDetailsModalAction[]) => {
    for (const action of actions) {
      switch (action.type) {
        case 'resetForm':
          dispatch(resetForm());
          break;
        case 'closeModal': {
          // Don't close if adjustOrder showed a presale message or availability modal
          const currentModal = selectCurrentModalId(store.getState());
          if (currentModal !== MESSAGE_MODAL_ID && currentModal !== ITEM_CART_AVAILABILITY_MODAL) {
            dispatch(setCurrentModal(null));
          }
          break;
        }
        case 'applyBufferDeliveryEstimate':
          dispatch(applyBufferDeliveryEstimate());
          break;
        case 'clearRecentLocations':
          dispatch(clearRecentLocations());
          break;
      }

      if (action.payload === undefined) {
        continue;
      }

      switch (action.type) {
        case 'updateForm':
          dispatch(updateForm(action.payload));
          break;
        case 'adjustOrder':
          // Log analytics when delivery flow is complete
          saleTypeSelected({ saleType, store: location?.name });
          await dispatch(adjustOrder(action.payload));
          if (selectCurrentModalId(store.getState())) {
            return;
          }
          break;
        case 'confirmLocationEstimate':
          dispatch(confirmLocationEstimate(action.payload));
          break;
        case 'setDeliveryNotes':
          dispatch(setDeliveryNotes(action.payload));
          break;
        case 'setDeliveryDetails':
          dispatch(setDeliveryDetails(action.payload));
          break;
        case 'setDesiredDeliveryAddress':
          dispatch(
            setDesiredDeliveryAddress(
              action.payload
                ? {
                    ...action.payload,
                    addressComponents: {
                      ...action.payload.addressComponents,
                      postCode: action.payload.addressComponents?.postCode ?? '',
                    },
                  }
                : action.payload,
            ),
          );
          break;
        case 'saveAddress':
          await dispatch(saveAddress(action.payload));
          break;
        case 'addToRecentLocations':
          dispatch(addToRecentLocations(action.payload));
          break;
        case 'setSelectedAddress':
          dispatch(setSelectedAddress(action.payload));
          break;
        case 'setInlineAlert':
          dispatch(setInlineAlert(action.payload));
          break;
        case 'setBufferDeliveryTime':
          dispatch(setBufferDeliveryTime(action.payload));
          break;
      }
    }
  };

  const goBack = async () => {
    dispatch(updateLoadingDependencies({ handleURLParamsInProgress: false }));

    const backButtonParams: BackButtonParams = {
      page: formState.page,
      addressSelectionView: formState.addressSelectionView,
      editingAddress: formState.editingAddress,
      adjustingPin: formState.adjustingPin,
      addressInput: formState.addressInput,
    };

    const actions = getBackButtonActionPlan(backButtonParams);

    await processActions(actions);
  };

  const onBackButtonClicked = async () => {
    if (formState.page === 'DeliveryDetailsConfirmation') {
      // Check if selectedAddress has changed since entering this page
      // We don't need to include displayAddress.secondLine because that value is evaluated elsewhere in the object
      const hasAddressChanged = !deepEqualIgnoringUndefined({
        initialValues: {
          ...initialSelectedAddressRef.current,
          displayAddress: {
            firstLine: initialSelectedAddressRef.current?.displayAddress?.firstLine,
          },
        } as DeliveryAddress,
        updatedValues: {
          ...formState.selectedAddress,
          displayAddress: { firstLine: formState.selectedAddress?.displayAddress?.firstLine },
        } as DeliveryAddress,
      });

      // The user has unsaved changes. Prompt for confirmation before navigating back.
      if (hasAddressChanged) {
        open({
          title: t('discardChangesModal.title'),
          content: (
            <DiscardChangesModal
              onCancel={() => {
                // Close the discard changes modal but do not go back.
                close();
              }}
              onDiscard={() => {
                // Close the discard changes modal and navigate back.
                dispatch(replaceSelectedAddress(initialSelectedAddressRef.current));
                close();
                goBack();
              }}
            />
          ),
          hideCloseButton: true,
        });
        return;
      }
    }
    goBack();
  };

  const onContinueButtonClicked = async () => {
    dispatch(updateLoadingDependencies({ handleURLParamsInProgress: false }));

    const params: ContinueButtonParams = {
      getDeliveryAddressFromPlaceIdAsyncFn: getDeliveryAddressFromPlaceIdAsync,
      page: formState.page,
      deliveryReadyToApply,
      deliveryRequiresLocationSelection,
      selectedLocationId,
      saleType,
      effectiveDeliveryTime,
      effectiveDeliveryAddress,
      addressSelectionView: formState.addressSelectionView,
      editingAddress: formState.editingAddress,
      selectedAddress: formState.selectedAddress,
      googleMapsApiKey,
      adjustingPin: formState.adjustingPin,
      pinCoordinates: formState.pinCoordinates,
      recentAddresses,
    };
    const preActions = await getContinueButtonActionPlan(params, 'preActions');
    await processActions(preActions);

    const actions = await getContinueButtonActionPlan(params);
    await processActions(actions);
  };

  const onToggleModal = () => {
    if (formState.page === 'DeliveryDetailsConfirmation') {
      // Check for unsaved changes
      const hasAddressChanged = !deepEqualIgnoringUndefined({
        initialValues: {
          ...initialSelectedAddressRef.current,
          displayAddress: {
            firstLine: initialSelectedAddressRef.current?.displayAddress?.firstLine,
          },
        } as DeliveryAddress,
        updatedValues: {
          ...formState.selectedAddress,
          displayAddress: { firstLine: formState.selectedAddress?.displayAddress?.firstLine },
        } as DeliveryAddress,
      });

      if (hasAddressChanged) {
        open({
          title: t('discardChangesModal.title'),
          content: (
            <DiscardChangesModal
              onCancel={close}
              onDiscard={() => {
                dispatch(replaceSelectedAddress(initialSelectedAddressRef.current));
                close();
                dispatch(setCurrentModal(null));
                dispatch(resetForm());
                dispatch(updateLoadingDependencies({ handleURLParamsInProgress: false }));
              }}
            />
          ),
          hideCloseButton: true,
        });
        return;
      }
    }

    dispatch(setCurrentModal(null));
    dispatch(resetForm());
    dispatch(updateLoadingDependencies({ handleURLParamsInProgress: false }));
  };

  // We are storing the modal content height in the form state so that the
  // Google Map component can adjust it's height accordingly. The Map requires
  // an explicit height to render at all, so we want to ensure the height of
  // the map is relative to the modal viewport.
  const modalContentRef = useRef<HTMLDivElement>(null);

  // Capture the initial selectedAddress when navigating to DeliveryDetailsConfirmation
  const initialSelectedAddressRef = useRef<DeliveryAddress | undefined>(undefined);
  useEffect(() => {
    if (formState.page === 'DeliveryDetailsConfirmation') {
      initialSelectedAddressRef.current = formState.selectedAddress;
    }
  }, [formState.page]);

  useLayoutEffect(() => {
    const updateHeight = () => {
      if (modalContentRef.current) {
        dispatch(updateForm({ modalHeight: modalContentRef.current.clientHeight }));
      }
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [formState.page]);

  const continueButtonDisabledParams: ContinueButtonDisabledParams = {
    keyOrderPropertyUpdateInProgress,
    submittingForm: formState.submittingForm,
    page: formState.page,
    effectiveDeliveryAddress,
    deliveryEstimateFailed,
    deliveryRequiresLocationSelection,
    selectedLocationId,
    locationDeliveryEstimates,
    adjustingPin: formState.adjustingPin,
    selectedAddress: formState.selectedAddress,
    addressSelectionView: formState.addressSelectionView,
    deliveryTimeSelected,
    saleType,
  };

  const continueButtonTextParams: ContinueButtonTextParams = {
    page: formState.page,
    addressSelectionView: formState.addressSelectionView,
    adjustingPin: formState.adjustingPin,
    editingAddress: formState.editingAddress,
    selectedAddress: formState.selectedAddress,
  };

  return (
    <Modal
      isOpen={true}
      scrollable
      fullscreen={deviceTypeMobile ? true : false}
      toggle={onToggleModal}
      fade={true}
      style={{ ...styles.modal, position: 'relative' }}
    >
      <div style={styles.modalHeaderContainer}>
        <ModalHeader backClick={onBackButtonClicked} title={getModalTitleText(formState)} />
      </div>

      <div ref={modalContentRef} style={styles.modalContentContainer}>
        {formState.page === 'DeliveryTimeAndAddressSelection' && (
          <DeliveryTimeAndAddressSelection
            saleType={saleType}
            selectedLocationId={selectedLocationId}
            setSelectedLocationId={setSelectedLocationId}
          />
        )}
        {formState.page === 'AddressLookupAndConfirmation' && (
          <AddressLookupAndConfirmation onOpenAddressInput={handleOpenAddressInput} />
        )}
        {formState.page === 'DeliveryDetailsConfirmation' && (
          <DeliveryDetailsConfirmation onOpenAddressInput={handleOpenAddressInput} />
        )}
      </div>

      {formState.page === 'AddressLookupAndConfirmation' &&
        formState.addressSelectionView === 'Lookup' && (
          <div style={styles.poweredByGoogleContainer}>
            <img src={poweredByGoogleSvg} alt="Powered by Google" />
          </div>
        )}

      <div
        style={combineStyles(
          styles.modalFooterContainer,
          deviceTypeMobile && styles.modalFooterContainerMobile,
        )}
      >
        <StandardButton
          label={getContinueButtonText(continueButtonTextParams)}
          disabled={isContinueButtonDisabled(continueButtonDisabledParams)}
          themeKey="continueButton"
          onClick={onContinueButtonClicked}
          showSpinner={isContinueButtonLoading(continueButtonDisabledParams)}
          style={styles.continueButton}
        />
      </div>
    </Modal>
  );
};

const DeliveryDetails = () => {
  const deviceTypeMobile = useAppSelector(getDeviceTypeMobile);
  return (
    <BottomSheetProvider deviceTypeMobile={deviceTypeMobile}>
      <Content />
    </BottomSheetProvider>
  );
};

const styles: Styles = {
  modal: {
    maxWidth: 660,
  },
  modalContentContainer: {
    overflow: 'auto',
    height: '100vh',
  },
  modalFooterContainer: {
    bottom: 0,
    width: '100%',
    padding: '20px 50px',
    borderTop: '1px solid #e0e0e0',
  },
  modalFooterContainerMobile: {
    padding: '30px',
  },
  poweredByGoogleContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 16,
    paddingBottom: 16,
  },
  continueButton: {
    maxWidth: 'none',
  },
};

export default DeliveryDetails;
